import {
  Button,
  Callout,
  Checkbox,
  Collapse,
  Icon,
  InputGroup,
  Label,
  Radio,
  RadioGroup,
  Tooltip
} from "@blueprintjs/core";
import styled from "@emotion/styled";
import {useCallback, useRef, useState} from "react";
import {FormMode} from "./InviteUserDialog";
import {DISPATCH_ADMINS, DISPATCH_TENANT_ADMIN, User} from "./UserManagement";
import {UserProfile} from "./UserProfile";
import {useAuthState} from "../../../providers/AuthProvider";
import {ConfirmationDialog} from "../../common/ConfirmationDialog";

interface UserFormProps {
  formMode: FormMode;
  formId: string;
  formData: UserProfile;
  emailValid: boolean;
  emailAlreadyExists: boolean;
  deletedUserRestorable: boolean;
  cmsHintDismissed: boolean;
  onDismissCmsHint: () => void;
  onFormChange: (formId: string, fieldName: keyof UserProfile, value: any) => void;
  onRemoveForm?: (formId: string) => void;
}

export interface InviteUserDialogRef {
  onOpen: () => void;
  onClose: () => void;
  onOpenUpdate: (userToEdit: User) => void;
}

const isDispatchAdmin = (user: UserProfile) => {
  return user.groups.includes(DISPATCH_TENANT_ADMIN);
};

const isDispatchSuperAdmin = (user: UserProfile) => {
  return user.groups.includes(DISPATCH_ADMINS);
};

const RADIO_USER = "user";
const RADIO_ADMIN = "admin";

const UserForm = ({
  formId,
  formData,
  formMode,
  emailValid,
  emailAlreadyExists,
  deletedUserRestorable,
  cmsHintDismissed,
  onDismissCmsHint,
  onFormChange,
  onRemoveForm
}: UserFormProps) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [selectedUserType, setSelectedUserType] = useState<string>(() =>
    isDispatchSuperAdmin(formData) || isDispatchAdmin(formData) ? RADIO_ADMIN : RADIO_USER
  );
  const [showCmsHint, setShowCmsHint] = useState(false);
  const [dontShowCmsHintAgain, setDontShowCmsHintAgain] = useState(false);
  const suppressNextFocusRef = useRef(false);

  const {isEcourierUser, tenant} = useAuthState();

  const closeCmsHint = useCallback(() => {
    // The dialog steals focus on open and Blueprint restores it to the input on close;
    // suppress that one restored focus so the hint does not immediately reopen.
    suppressNextFocusRef.current = true;
    setShowCmsHint(false);
  }, []);

  const handleCmsHintOkay = useCallback(() => {
    // Only the "Okay" button persists the choice; X / click-outside / Esc just close.
    if (dontShowCmsHintAgain) {
      onDismissCmsHint();
    }
    closeCmsHint();
  }, [dontShowCmsHintAgain, onDismissCmsHint, closeCmsHint]);

  const handleUsernameFocus = useCallback(() => {
    if (suppressNextFocusRef.current) {
      suppressNextFocusRef.current = false;
      return;
    }
    if (!cmsHintDismissed) {
      setDontShowCmsHintAgain(false);
      setShowCmsHint(true);
    }
  }, [cmsHintDismissed]);

  const onRoleRadioChange = useCallback(
    (e: any) => {
      let updatedGroups = [...formData.groups];
      setSelectedUserType(e.target.value);
      if (e.target.value === RADIO_ADMIN) {
        updatedGroups.push(DISPATCH_TENANT_ADMIN);
      } else {
        const index = formData.groups.indexOf(DISPATCH_TENANT_ADMIN);
        if (index > -1) {
          updatedGroups = [...formData.groups.slice(0, index), ...formData.groups.slice(index + 1)];
        }
      }
      onFormChange(formId, e.target.name, updatedGroups);
    },
    [formData.groups, formId, onFormChange]
  );

  const onFieldChange = useCallback(
    (e) => {
      onFormChange(formId, e.target.name, e.target.value);
    },
    [formId, onFormChange]
  );

  const handleExpand = useCallback(() => {
    if (formMode === FormMode.edit) {
      return;
    }
    setIsExpanded(!isExpanded);
  }, [formMode, isExpanded]);

  return (
    <Container>
      <Header>
        <StyledButton variant="minimal" icon={isExpanded ? "chevron-up" : "chevron-down"} onClick={handleExpand}>
          {formData.email.trim() || "New User"}
        </StyledButton>
        {formMode === FormMode.new && onRemoveForm && (
          <Button
            data-testid="delete-user-form"
            className="delete-button"
            variant="minimal"
            icon={<Icon icon="trash" size={12} />}
            onClick={() => onRemoveForm(formId)}
          />
        )}
      </Header>
      <Collapse keepChildrenMounted isOpen={isExpanded}>
        <Form data-testid={"form-container"}>
          <Label inputMode={"text"}>
            <StyledLabel isRequired>First Name</StyledLabel>
            <InputGroup
              data-testid={"create-firstName"}
              name={"firstName"}
              placeholder={"First name"}
              fill={false}
              onChange={onFieldChange}
              value={formData.firstName}
            />
          </Label>
          <Label inputMode={"text"}>
            <StyledLabel isRequired>Last Name</StyledLabel>
            <InputGroup
              data-testid={"create-lastName"}
              name={"lastName"}
              placeholder={"Last name"}
              fill={false}
              onChange={onFieldChange}
              value={formData.lastName}
            />
          </Label>
          <Label inputMode={"email"}>
            <StyledLabel isRequired>Email Address</StyledLabel>
            <InputGroup
              data-testid={"create-email"}
              name={"email"}
              placeholder={"Email address"}
              fill={false}
              onChange={onFieldChange}
              value={formData.email}
              readOnly={formMode === FormMode.edit && isEcourierUser}
              intent={(formData.email.length > 0 && !emailValid) || emailAlreadyExists ? "danger" : "none"}
            />
            {formData.email.length > 0 && !emailValid && <ErrorText>Please enter a valid email address</ErrorText>}
            {emailAlreadyExists && (
              <ErrorText>This email is already used for another account. Please enter a different email.</ErrorText>
            )}
          </Label>
          {deletedUserRestorable && (
            <RestoreNotice compact intent="primary">
              This email belongs to a user who was removed from Dispatch. Click Resend Invitation to restore their
              account to {tenant?.shortName}.
            </RestoreNotice>
          )}
          <Label inputMode={"tel"}>
            <StyledLabel>Phone</StyledLabel>
            <InputGroup
              data-testid={"create-phone"}
              name={"phone"}
              placeholder={"Phone"}
              fill={false}
              onChange={onFieldChange}
              value={formData.phone}
            />
          </Label>
          <Label>
            <StyledLabel isRequired>e-Courier Username</StyledLabel>
            <InputGroup
              data-testid={"create-legacyUserId"}
              name={"legacyUserId"}
              placeholder={"e-Courier Username"}
              fill={false}
              onChange={onFieldChange}
              onFocus={handleUsernameFocus}
              value={formData.legacyUserId}
            />
          </Label>
          <ConfirmationDialog
            testIdPrefix={"cms-username-hint"}
            isVisible={showCmsHint}
            header={"Ensure Username matches CMS"}
            body={
              <>
                <div>
                  The username must match the username of the user in our platform (CMS). If it doesn&apos;t match,
                  functions will not work as expected.
                </div>
                <HintCheckbox
                  data-testid={"cms-username-hint-dont-show-again"}
                  label={"Do not show again"}
                  checked={dontShowCmsHintAgain}
                  onChange={(e) => setDontShowCmsHintAgain(e.currentTarget.checked)}
                />
              </>
            }
            hideNoButton
            yesText={"Okay"}
            yesTextColor={"#137cbd"}
            handleYesClicked={handleCmsHintOkay}
            handleClose={closeCmsHint}
          />
          <Label style={{marginTop: "18px"}}>
            <Tooltip content={"Dispatch Admins can manage and invite other Dispatch Users"} hoverOpenDelay={1000}>
              <RadioGroup
                name={"groups"}
                disabled={isDispatchSuperAdmin(formData)}
                onChange={onRoleRadioChange}
                selectedValue={selectedUserType}
                inline
              >
                <Radio label="Dispatch User" value={RADIO_USER} data-testid={"is-user-radio-btn"} />
                <Radio label="Dispatch Admin" value={RADIO_ADMIN} data-testid={"is-admin-radio-btn"} />
              </RadioGroup>
            </Tooltip>
          </Label>
          <InputGroup
            data-testid={"user-id"}
            name={"id"}
            placeholder={"Id"}
            fill={false}
            value={formData.id}
            hidden={true}
          />
        </Form>
      </Collapse>
    </Container>
  );
};

export default UserForm;

const Container = styled.div`
  border-radius: 8px;
  padding: 8px 0;

  .delete-button {
    display: none;
  }

  :hover {
    background-color: #dddddd53;

    .delete-button {
      display: flex;
    }
  }

  transition: background-color 0.2s ease-in-out;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  padding-right: 10px;
`;
const StyledLabel = styled.div<{isRequired?: boolean}>`
  font-size: 13px;
  :after {
    content: " *";
    color: #fa545e;
    display: ${(props) => (props.isRequired ? "inline" : "none")};
  }
`;

const ErrorText = styled.div`
  color: #fa545e;
  font-size: 12px;
  margin-top: 4px;
`;

const RestoreNotice = styled(Callout)`
  margin-top: 6px;
  font-size: 12px;
  line-height: 16px;
`;

const HintCheckbox = styled(Checkbox)`
  margin-top: 12px;
  margin-bottom: 0;
`;

const Form = styled.form`
  padding: 0 30px 8px;

  .bp5-input {
    border-radius: 4px;
    box-shadow: none;
    border: 1px solid #797979;
  }

  .bp5-label {
    margin-top: 12px;
    margin-bottom: 0;
  }
`;

const StyledButton = styled(Button)`
  display: flex;
  justify-content: left;
  width: 100%;
  font-weight: 500;
  background: none;

  :hover {
    background: none !important;
  }
`;
